import React, { useState, useEffect } from "react";
import { FileUpload } from "../assets/file-upload";

export interface AIContentSource {
  type: "DESTINATION_URL" | "UPLOADED_FILE";
  destinationId?: string;
  fileId?: string;
  analysisStatus?: "PENDING" | "COMPLETED" | "FAILED";
}

export interface CalendlyIntegration {
  enabled: boolean;
  calendarLink?: string;
  connectionStatus?: "CONNECTED" | "FAILED" | "NOT_CONFIGURED";
}

export interface AISettings {
  model: "NOVA_PRO" | "NOVA_LITE";
  temperature: number;
  maxTokens: number;
  systemPrompt?: string;
}

export interface AIConfiguration {
  aiChatEnabled: boolean;
  contentSource?: AIContentSource;
  calendlyIntegration?: CalendlyIntegration;
  aiSettings: AISettings;
}

interface AIConfigurationProps {
  configuration: AIConfiguration;
  onChange: (config: AIConfiguration) => void;
  availableDestinations?: Array<{
    id: string;
    name: string;
    url: string;
  }>;
  disabled?: boolean;
  assetId?: string; // Optional asset ID for file uploads
}

export function AIChatConfiguration({
  configuration,
  onChange,
  availableDestinations = [],
  disabled = false,
  assetId,
}: AIConfigurationProps) {
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Debug: Log when configuration prop changes
  useEffect(() => {
    console.log("🎛️ AIChatConfiguration received new configuration:", {
      aiChatEnabled: configuration.aiChatEnabled,
      hasContentSource: !!configuration.contentSource,
      contentSourceType: configuration.contentSource?.type,
      contentSourceFileId: configuration.contentSource?.fileId,
      hasCalendlyIntegration: !!configuration.calendlyIntegration,
      calendlyEnabled: configuration.calendlyIntegration?.enabled,
      calendlyLink: configuration.calendlyIntegration?.calendarLink,
    });
  }, [configuration]);

  const handleAIEnabledChange = (enabled: boolean) => {
    onChange({
      ...configuration,
      aiChatEnabled: enabled,
      // Reset content source if disabling AI
      contentSource: enabled ? configuration.contentSource : undefined,
    });
  };

  const handleContentSourceChange = (contentSource: AIContentSource) => {
    onChange({
      ...configuration,
      contentSource,
    });
  };

  const handleCalendlyChange = (calendlyIntegration: CalendlyIntegration) => {
    onChange({
      ...configuration,
      calendlyIntegration,
    });
  };

  const handleAISettingsChange = (aiSettings: Partial<AISettings>) => {
    onChange({
      ...configuration,
      aiSettings: {
        ...configuration.aiSettings,
        ...aiSettings,
      },
    });
  };

  const handleFilesUploaded = (files: any[]) => {
    if (files.length > 0) {
      const file = files[0];
      handleContentSourceChange({
        type: "UPLOADED_FILE",
        fileId: file.id || file.fileId,
        analysisStatus: "PENDING",
      });
    }
  };

  const testCalendlyConnection = async () => {
    if (!configuration.calendlyIntegration?.calendarLink) {
      setConnectionTestResult({
        success: false,
        message: "Please provide a calendar link",
      });
      return;
    }

    setTestingConnection(true);
    setConnectionTestResult(null);

    try {
      // Simulate API call - in real implementation, this would call the backend
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock success for now
      setConnectionTestResult({
        success: true,
        message: "Connection successful! Calendar integration is working.",
      });

      handleCalendlyChange({
        enabled: configuration.calendlyIntegration!.enabled,
        calendarLink: configuration.calendlyIntegration!.calendarLink,
        connectionStatus: "CONNECTED",
      });
    } catch (error) {
      setConnectionTestResult({
        success: false,
        message: "Connection failed. Please check your calendar link.",
      });

      handleCalendlyChange({
        enabled: configuration.calendlyIntegration!.enabled,
        calendarLink: configuration.calendlyIntegration!.calendarLink,
        connectionStatus: "FAILED",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const isContentSourceValid = () => {
    if (!configuration.contentSource) return false;

    if (configuration.contentSource.type === "DESTINATION_URL") {
      return !!configuration.contentSource.destinationId;
    }

    if (configuration.contentSource.type === "UPLOADED_FILE") {
      return !!configuration.contentSource.fileId;
    }

    return false;
  };

  const getValidationErrors = () => {
    const errors: string[] = [];

    if (configuration.aiChatEnabled && !isContentSourceValid()) {
      errors.push("Content source is required when AI Chat is enabled");
    }

    if (configuration.calendlyIntegration?.enabled) {
      if (!configuration.calendlyIntegration.calendarLink) {
        errors.push("Calendly calendar link is required");
      }
    }

    return errors;
  };

  const validationErrors = getValidationErrors();

  return (
    <div className="space-y-6" data-testid="ai-chat-configuration">
      {/* AI Chat Enable/Disable */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">AI Chat</h3>
          <p className="text-sm text-gray-600">
            Enable intelligent conversations
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={configuration.aiChatEnabled}
            onChange={(e) => handleAIEnabledChange(e.target.checked)}
            disabled={disabled}
            className="sr-only peer"
            data-testid="ai-chat-enabled-toggle"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {configuration.aiChatEnabled && (
        <div className="space-y-6 pl-4 border-l-2 border-blue-200">
          {/* Content Source Selection */}
          <div className="space-y-4">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">
                Content Source <span className="text-red-500">*</span>
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Choose the content that the AI will use to answer questions
              </p>
            </div>

            <div className="space-y-3">
              {/* Destination URL Option */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="contentSource"
                    value="DESTINATION_URL"
                    checked={
                      configuration.contentSource?.type === "DESTINATION_URL"
                    }
                    onChange={() =>
                      handleContentSourceChange({
                        type: "DESTINATION_URL",
                        analysisStatus: "PENDING",
                      })
                    }
                    disabled={disabled}
                    className="mt-1"
                    data-testid="content-source-destination"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      <span className="font-medium text-gray-900">
                        Destination URL
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Use content from one of your conversation destinations
                    </p>

                    {configuration.contentSource?.type ===
                      "DESTINATION_URL" && (
                      <div className="mt-3">
                        <select
                          value={
                            configuration.contentSource.destinationId || ""
                          }
                          onChange={(e) =>
                            handleContentSourceChange({
                              type: "DESTINATION_URL",
                              destinationId: e.target.value,
                              analysisStatus: "PENDING",
                            })
                          }
                          disabled={disabled}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          data-testid="destination-selector"
                        >
                          <option value="">Select a destination...</option>
                          {availableDestinations.map((dest) => (
                            <option key={dest.id} value={dest.id}>
                              {dest.name} - {dest.url}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* File Upload Option */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="contentSource"
                    value="UPLOADED_FILE"
                    checked={
                      configuration.contentSource?.type === "UPLOADED_FILE"
                    }
                    onChange={() =>
                      handleContentSourceChange({
                        type: "UPLOADED_FILE",
                        analysisStatus: "PENDING",
                      })
                    }
                    disabled={disabled}
                    className="mt-1"
                    data-testid="content-source-file"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="font-medium text-gray-900">
                        Upload File
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Upload a PDF, document, or other file for the AI to
                      analyze
                    </p>

                    {configuration.contentSource?.type === "UPLOADED_FILE" && (
                      <div className="mt-3">
                        <FileUpload
                          onFilesUploaded={handleFilesUploaded}
                          acceptedTypes={[
                            "application/pdf",
                            "text/plain",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                          ]}
                          maxFileSize={10 * 1024 * 1024}
                          disabled={disabled}
                        />
                        {configuration.contentSource.fileId && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                            File uploaded successfully! Analysis will begin when
                            the conversation is created.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Calendly Integration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-md font-medium text-gray-900">
                  Calendly Integration
                </h4>
                <p className="text-sm text-gray-600">
                  Allow the AI to help users schedule appointments
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={configuration.calendlyIntegration?.enabled || false}
                  onChange={(e) =>
                    handleCalendlyChange({
                      enabled: e.target.checked,
                      calendarLink:
                        configuration.calendlyIntegration?.calendarLink || "",
                      connectionStatus: "NOT_CONFIGURED",
                    })
                  }
                  disabled={disabled}
                  className="sr-only peer"
                  data-testid="calendly-enabled-toggle"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {configuration.calendlyIntegration?.enabled && (
              <div className="space-y-4 pl-4 border-l-2 border-orange-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calendar Link <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={configuration.calendlyIntegration.calendarLink || ""}
                    onChange={(e) =>
                      handleCalendlyChange({
                        enabled: configuration.calendlyIntegration!.enabled,
                        calendarLink: e.target.value,
                        connectionStatus: "NOT_CONFIGURED",
                      })
                    }
                    disabled={disabled}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://calendly.com/your-username/meeting"
                    data-testid="calendly-calendar-link"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your public Calendly scheduling link
                  </p>
                </div>

                {/* Connection Test */}
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={testCalendlyConnection}
                    disabled={
                      disabled ||
                      testingConnection ||
                      !configuration.calendlyIntegration.calendarLink
                    }
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                    data-testid="test-calendly-connection"
                  >
                    {testingConnection ? "Testing..." : "Test Connection"}
                  </button>

                  {configuration.calendlyIntegration.connectionStatus ===
                    "CONNECTED" && (
                    <div className="flex items-center text-green-600 text-sm">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Connected
                    </div>
                  )}

                  {configuration.calendlyIntegration.connectionStatus ===
                    "FAILED" && (
                    <div className="flex items-center text-red-600 text-sm">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L10 11.414l2.707-2.707a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Failed
                    </div>
                  )}
                </div>

                {connectionTestResult && (
                  <div
                    className={`p-3 rounded-lg text-sm ${
                      connectionTestResult.success
                        ? "bg-green-50 border border-green-200 text-green-700"
                        : "bg-red-50 border border-red-200 text-red-700"
                    }`}
                  >
                    {connectionTestResult.message}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Advanced AI Settings */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800"
              data-testid="toggle-advanced-settings"
            >
              <svg
                className={`w-4 h-4 transition-transform ${
                  showAdvancedSettings ? "rotate-90" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span>Advanced AI Settings</span>
            </button>

            {showAdvancedSettings && (
              <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Model
                  </label>
                  <select
                    value={configuration.aiSettings.model}
                    onChange={(e) =>
                      handleAISettingsChange({
                        model: e.target.value as "NOVA_PRO" | "NOVA_LITE",
                      })
                    }
                    disabled={disabled}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="ai-model-selector"
                  >
                    <option value="NOVA_LITE">
                      Nova Lite (Faster, Lower Cost)
                    </option>
                    <option value="NOVA_PRO">
                      Nova Pro (More Capable, Higher Cost)
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature: {configuration.aiSettings.temperature}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={configuration.aiSettings.temperature}
                    onChange={(e) =>
                      handleAISettingsChange({
                        temperature: parseFloat(e.target.value),
                      })
                    }
                    disabled={disabled}
                    className="w-full"
                    data-testid="ai-temperature-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>More Focused</span>
                    <span>More Creative</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    min="100"
                    max="4000"
                    step="100"
                    value={configuration.aiSettings.maxTokens}
                    onChange={(e) =>
                      handleAISettingsChange({
                        maxTokens: parseInt(e.target.value),
                      })
                    }
                    disabled={disabled}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="ai-max-tokens"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum length of AI responses (100-4000 tokens)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    System Prompt (Optional)
                  </label>
                  <textarea
                    value={configuration.aiSettings.systemPrompt || ""}
                    onChange={(e) =>
                      handleAISettingsChange({
                        systemPrompt: e.target.value,
                      })
                    }
                    disabled={disabled}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Custom instructions for the AI agent..."
                    data-testid="ai-system-prompt"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Additional instructions to customize the AI's behavior
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L10 11.414l2.707-2.707a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Configuration Issues
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
